import type { InputMaybe, Lookalike_Definition_Bool_Exp } from '@openx/types/gqlTypes.ts';
import type { Provider } from '@openx/types/provider.ts';
import type { SegmentFiltersQueryParams } from '../../components/drawer/components/SegmentsList/types.ts';
import { and, buildRangeFilter, clean, eq, ilike, in_, type WhereCondition } from './filterHelpers.ts';

export type BuildLookalikeFiltersParams = {
  filters: SegmentFiltersQueryParams;
  accountId: string;
  linkedProviders: Provider[];
  selectedLookalikeDefinitions?: string[];
};

const buildLookalikeNameFilter = (filters: SegmentFiltersQueryParams): WhereCondition => {
  return ilike('lookalike_name', filters.phrase);
};

const buildLookalikeReachFilter = (filters: SegmentFiltersQueryParams): WhereCondition => {
  if (!filters.reach) return undefined;

  const reachRangeFilter = buildRangeFilter('reach', filters.reach);

  if (!reachRangeFilter) return undefined;

  return {
    lookalike_segment_seed_aggregate: {
      segment: reachRangeFilter,
    },
    lookalike_segment_similarities: { segment: reachRangeFilter },
  };
};

const buildLookalikeProviderFilter = (linkedProviders: Provider[], dataProvider?: string[]) => {
  const linkedProviderIds: string[] = linkedProviders?.map(provider => provider.id);

  if (dataProvider && dataProvider.length > 0) {
    return in_('provider_id', dataProvider);
  }

  return linkedProviderIds && in_('provider_id', linkedProviderIds);
};

const buildLookalikeDefinitionIdFilter = (definitionIds: string[]) => {
  return in_('id', definitionIds);
};

const getLookalikeAlwaysAppliedFilters = (accountId: string): WhereCondition[] => {
  const filters: WhereCondition[] = [];

  const availableBuildAudienceFilter = eq('available_build_audience', true);
  const isArchivedFilter = eq('is_archived', false);
  const accountFilter = eq('account_id', accountId);

  if (availableBuildAudienceFilter) filters.push(availableBuildAudienceFilter);
  if (isArchivedFilter) filters.push(isArchivedFilter);
  if (accountFilter) filters.push(accountFilter);

  return filters;
};

export const buildLookalikeFilters = ({
  filters,
  accountId,
  linkedProviders,
  selectedLookalikeDefinitions,
}: BuildLookalikeFiltersParams) => {
  const alwaysFilters = getLookalikeAlwaysAppliedFilters(accountId);
  const providerFilter = buildLookalikeProviderFilter(linkedProviders, filters.dataProvider);

  const filterClauses: WhereCondition[] = [
    buildLookalikeNameFilter(filters),
    buildLookalikeReachFilter(filters),
    selectedLookalikeDefinitions ? buildLookalikeDefinitionIdFilter(selectedLookalikeDefinitions) : undefined,
  ].filter(clause => clause !== undefined);

  const whereClause = and(...alwaysFilters, providerFilter, ...filterClauses);
  // Cast the where clauses to ensure they're compatible with GraphQL input types
  const cleanedWhereClause = clean(whereClause) as InputMaybe<Lookalike_Definition_Bool_Exp>;

  return {
    where: cleanedWhereClause,
    whereAggregate: cleanedWhereClause,
    order_by: { updated_date: 'desc' },
    limit: filters.limit,
    offset: filters.offset,
  };
};
